/**
 * Severity — severity indicator using **icon + shape + color**, not color
 * alone. Readable in grayscale, colorblind-safe.
 *
 * Pattern from PagerDuty (octagon=critical, diamond=warn) + Datadog (filled
 * triangle=critical, hollow circle=warn) + ISA-101 SCADA (gray baseline,
 * color earns its place).
 *
 * Use when: rendering an incident priority, alarm severity, or any signal
 * where colorblind operators need a non-color cue. For neutral status
 * (online/offline) use `StatusBadge` — those don't need shape encoding.
 */
import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { useLocale } from '../i18n/useLocale';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

const META: Record<SeverityLevel, { labelKey: string; shape: string; tone: 'critical' | 'high' | 'medium' | 'low' | 'info' }> = {
  critical: { labelKey: 'widget.severity.critical', shape: '▲', tone: 'critical' },  // filled triangle
  high:     { labelKey: 'widget.severity.high',     shape: '◆', tone: 'high' },      // diamond
  medium:   { labelKey: 'widget.severity.medium',   shape: '●', tone: 'medium' },    // filled circle
  low:      { labelKey: 'widget.severity.low',      shape: '○', tone: 'low' },       // hollow circle
  info:     { labelKey: 'widget.severity.info',     shape: '■', tone: 'info' },      // square
};

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  shape: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1,
    /* Make sure the shape itself stays visible at print/grayscale by relying on glyph */
  },
  /* Tones — sorted by luminance so colorblind operators still see a gradient */
  critical: { color: tokens.colorPaletteRedForeground1 },
  high:     { color: tokens.colorPaletteDarkOrangeForeground1 },
  medium:   { color: tokens.colorPaletteYellowForeground1 },
  low:      { color: tokens.colorBrandForeground1 },
  info:     { color: tokens.colorNeutralForeground3 },
  /* Compact mode — shape only, label hidden */
  compact: {
    gap: 0,
  },
  /* Stripe variant — left-edge bar (Datadog/Honeycomb pattern, not fill) */
  stripe: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderLeft: `3px solid currentColor`,
  },
});

interface SeverityProps {
  level: SeverityLevel;
  /** Hide the text label — shape + color only. Useful in tight tables. */
  compact?: boolean;
  /** Render as a left-edge stripe rather than inline indicator. */
  stripe?: boolean;
  /** Custom label override (defaults to "P1 Critical" etc.). */
  label?: string;
}

export function Severity({ level, compact, stripe, label }: SeverityProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const meta = META[level];
  const text = label ?? t(meta.labelKey);

  const content = (
    <span className={mergeClasses(styles.root, styles[meta.tone], compact && styles.compact, stripe && styles.stripe)}
      role="status"
      aria-label={text}
    >
      <span className={styles.shape} aria-hidden="true">{meta.shape}</span>
      {!compact ? <span>{text}</span> : null}
    </span>
  );

  if (compact) {
    return (
      <Tooltip content={text} relationship="description">
        {content}
      </Tooltip>
    );
  }
  return content;
}
